import React, { useMemo } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  SalesRecordingStatus, SalesRecordingType,
} from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { Doc } from '@marageti/z4-sdk/lib/documents';
import { OutlinedIcons, copyToClipboard } from '@marageti/z4-lib';
import { useNavigate } from 'react-router-dom';
import { StatusChip } from '../utils';
import { formatDateSold } from '../../../utils/sales';
import { SalesRecordingWithTripMatch } from '../../../hooks/use-sales-recordings-with-trip-matches';

type Props = {
  salesRecordings: SalesRecordingWithTripMatch[],
  onClickDocument?: (document: Doc) => void ;
  updateStatus: (salesRecordingId: string, salesRecordingStatus: SalesRecordingStatus) => void;
  totalSales: string | number | undefined;
  totalCommission: string | number | undefined;
};

type Row = {
  id: string;
  recordedOn: string;
  tripId: string;
  matched: string;
  traveler: string;
  type: string;
  totalPrice: {
    amount: string;
  };
  commission: {
    amount: string;
    notes: string;
  };
  commissionPercentage: string;
  doc: Doc | undefined | null;
  status: SalesRecordingStatus;
  statusReason: string;
  partnerSalesRecordingInvoiceDetailUrl: string | null;
  internalNote: string;
};

const typeLabel = {
  [SalesRecordingType.Adjustment]: 'Sales Adjustment',
  [SalesRecordingType.General]: 'Standard Sale',
  [SalesRecordingType.PartnerRepeat]: 'Repeat Sale',
  [SalesRecordingType.TravelerReferral]: 'Referral Sale',
  [SalesRecordingType.CommissionAfterTravel]: 'Commission After Travel',
  [SalesRecordingType.PlanningFee]: 'Planning Fee',
  [SalesRecordingType.RefundCredit]: 'Refund Credit',
};

const PartnerInvoiceTable = ({
  salesRecordings,
  onClickDocument = undefined,
  updateStatus,
  totalSales,
  totalCommission,
}: Props) => {
  const {
    OutlinedCloudDownloadIcon,
    OutlinedChatAltIcon,
    OutlinedDocumentDuplicateIcon,
  } = OutlinedIcons;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const rows: Row[] = useMemo(() => salesRecordings.map((sale) => {
    const partnerSalesRecordingInvoiceDetailUrl = `/sales/invoices/${sale.partnerId}/${sale.id}`;

    const formattedTotalPrice = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(sale.price.amountUSD);

    const formattedCommission = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(sale.commission.amountUSD);
    const commissionNotes = sale.priceComments || 'No comments';

    return {
      id: sale.id,
      recordedOn: formatDateSold(sale.createdAt),
      tripId: sale.referenceId,
      matched: sale.tripMatch?.createdAt ? formatDateSold(sale.tripMatch?.createdAt) : '-',
      traveler: sale.travelers?.[0]?.fullName || sale.travelers?.[0]?.id,
      type: typeLabel[sale.salesRecordingType],
      totalPrice: {
        amount: formattedTotalPrice,
      },
      commission: {
        amount: formattedCommission,
        notes: commissionNotes,
      },
      commissionPercentage: sale.price.amountUSD
        ? ((sale.commission.amountUSD / sale.price.amountUSD) * 100).toFixed(1)
        : '0.0',
      partnerSalesRecordingInvoiceDetailUrl,
      doc: sale?.document || null,
      status: sale.status,
      statusReason: sale.statusInfo.reason,
      internalNote: sale.internalNote,
    };
  }), [salesRecordings]);

  const handleRowClick = (row: Row) => () => {
    if (row.partnerSalesRecordingInvoiceDetailUrl) {
      navigate(row.partnerSalesRecordingInvoiceDetailUrl);
    }
  };

  return (
    <TableContainer sx={{ marginTop: '0px !important' }}>
      <Table sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell>Recorded</TableCell>
            <TableCell>Trip ID</TableCell>
            <TableCell>Matched</TableCell>
            <TableCell>Traveler</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Total Price</TableCell>
            <TableCell>Commission</TableCell>
            <TableCell>Comm. %</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Doc</TableCell>
            <TableCell>Approve</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              hover
              key={row.id}
              onClick={handleRowClick(row)}
              sx={{
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <TableCell sx={{ minWidth: '120px' }}>{row.recordedOn}</TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                {row.tripId && (
                <Box component="span" display="flex" alignItems="center">
                  {row.tripId}
                  <Tooltip title="Copy Trip ID to clipboard" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        if (row.tripId) {
                          e.stopPropagation();
                          copyToClipboard(row.tripId);
                          enqueueSnackbar('Copied!', { variant: 'success' });
                        }
                      }}
                      aria-label="copy trip ID"
                      sx={{ fontSize: '1.1rem', color: 'primary.main' }}
                    >
                      <OutlinedDocumentDuplicateIcon color="primary" fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                )}
                {!row.tripId && '-'}
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>{row.matched}</TableCell>
              <TableCell sx={{ minWidth: '150px' }}>{row.traveler}</TableCell>
              <TableCell sx={{ minWidth: '100px' }}>{row.type}</TableCell>
              <TableCell sx={{ color: row.totalPrice.amount.startsWith('-') ? 'error.main' : undefined }}>{row.totalPrice.amount}</TableCell>
              <Tooltip title={row.commission.notes} placement="top">
                <TableCell sx={{ color: row.commission.amount.startsWith('-') ? 'error.main' : undefined }}>{row.commission.amount}</TableCell>
              </Tooltip>
              <TableCell sx={{ fontFamily: 'captionBold' }}>
                {row.commissionPercentage}
                %
              </TableCell>
              <Tooltip title={row.internalNote} placement="top">
                <TableCell>
                  {row.internalNote && (
                  <OutlinedChatAltIcon sx={{ width: '24px', height: '24px', color: 'primary.main' }} />
                  )}
                </TableCell>
              </Tooltip>
              <TableCell>
                {row.doc && (
                <Box onClick={(e) => {
                  e.stopPropagation();
                  if (onClickDocument && row.doc) {
                    onClickDocument(row.doc);
                  }
                }}
                >
                  <OutlinedCloudDownloadIcon sx={{ width: '24px', height: '24px', color: 'primary.main' }} />
                </Box>
                )}
              </TableCell>
              <TableCell>
                <StatusChip
                  status={row.status}
                  onClick={row.status === SalesRecordingStatus.SoldStatus || row.status === SalesRecordingStatus.VerifiedStatus
                    ? () => updateStatus(row.id, row.status) : undefined}
                  showTooltip
                  tooltipCustomMessage={row.status === SalesRecordingStatus.ErrorStatus ? row.statusReason : ''}
                />
              </TableCell>
              <TableCell />
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell sx={{ fontFamily: 'captionBold' }}>Totals</TableCell>
            <TableCell sx={{ fontFamily: 'captionBold' }}>{totalSales}</TableCell>
            <TableCell sx={{ fontFamily: 'captionBold' }}>{totalCommission}</TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default PartnerInvoiceTable;
